import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import useGoBack from "@/hooks/useGoBack";
import useTranslations from "@/i18n/useTranslations";
import BackArrowIcon from "@/shared-components/icons/components/public/BackArrowIcon";
import SuarteName from "@/shared-components/icons/components/public/SuarteName";
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import Heading from "@/shared-components/text/components/Heading";
import Text from "@/shared-components/text/components/Text";

import "./index.css";

export default function BuyerProtectionPage() {
  const t = useTranslations("common.buyer_protection");
  const goBackHandler = useGoBack("/");
  const [activeSection, setActiveSection] = useState("overview");
  const contentRef = useRef(null);
  const subnavRef = useRef(null);
  const manualScrollRef = useRef(false);
  const manualScrollTimerRef = useRef(null);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return undefined;
    }

    const docEl = document.scrollingElement || document.documentElement;
    const isScrollable =
      content.scrollHeight > content.clientHeight &&
      window.getComputedStyle(content).overflowY !== "visible";
    const container = isScrollable ? content : window;
    const scrollTarget = container === window ? window : content;

    const sections = Array.from(content.querySelectorAll("[data-section]"));
    if (sections.length === 0) {
      return undefined;
    }
    setActiveSection(sections[0].id);

    let ticking = false;
    const getScrollTop = () =>
      container === window
        ? window.scrollY || docEl.scrollTop
        : content.scrollTop;
    const getClientHeight = () =>
      container === window ? window.innerHeight : content.clientHeight;
    const getScrollHeight = () =>
      container === window ? docEl.scrollHeight : content.scrollHeight;
    const getSectionTop = (section) => {
      if (container === window) {
        return section.getBoundingClientRect().top;
      }
      return (
        section.getBoundingClientRect().top -
        content.getBoundingClientRect().top
      );
    };
    const updateActive = () => {
      ticking = false;
      if (manualScrollRef.current) {
        return;
      }
      const scrollTop = getScrollTop();
      if (scrollTop === 0) {
        setActiveSection(sections[0].id);
        return;
      }
      const bottomThreshold = 24;
      const maxScroll = getScrollHeight() - getClientHeight();
      if (scrollTop >= maxScroll - bottomThreshold) {
        const lastSection = sections[sections.length - 1];
        if (lastSection) {
          setActiveSection(lastSection.id);
          return;
        }
      }
      const offset = getClientHeight() * 0.25;
      let closest = null;
      sections.forEach((section) => {
        const distance = Math.abs(getSectionTop(section) - offset);
        if (!closest || distance < closest.distance) {
          closest = { id: section.id, distance };
        }
      });
      if (closest?.id) {
        setActiveSection(closest.id);
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateActive);
      }
    };
    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateActive();
    return () => {
      scrollTarget.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (manualScrollTimerRef.current) {
        clearTimeout(manualScrollTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const nav = subnavRef.current;
    if (!nav) {
      return;
    }
    const activeLink = nav.querySelector(`a[href="#${activeSection}"]`);
    if (!activeLink) {
      return;
    }
    const navRect = nav.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    const linkOffset = linkRect.left - navRect.left;
    const targetScroll =
      nav.scrollLeft + linkOffset - (navRect.width / 2 - linkRect.width / 2);
    nav.scrollTo({ left: targetScroll, behavior: "smooth" });
  }, [activeSection]);

  const navLinks = [
    { href: "#overview", label: t("navigation.overview") },
    { href: "#coverage", label: t("navigation.coverage") },
    { href: "#pillars", label: t("navigation.pillars") },
    { href: "#flow", label: t("navigation.flow") },
    { href: "#cancellation", label: t("navigation.cancellation") },
    { href: "#returns", label: t("navigation.returns") },
    { href: "#resale", label: t("navigation.resale") },
    { href: "#shipping", label: t("navigation.shipping") },
    { href: "#privacy", label: t("navigation.privacy") },
    { href: "#faqs", label: t("navigation.faq") },
  ];

  return (
    <div className="bp-page">
      <StaticSeoHead
        title={t("seo.title")}
        description={t("seo.description")}
        canonicalPath="/buyer-protection"
        breadcrumbItems={[
          { name: "Home", url: "https://suarte.art/" },
          {
            name: t("title"),
            url: "https://suarte.art/buyer-protection",
          },
        ]}
      />
      <header className="bp-page__header">
        <div className="bp-page__header-inner">
          <BackArrowIcon
            className="bp-page__back-button"
            onClick={goBackHandler}
          />
          <SuarteName className="bp-page__brand" />
        </div>
      </header>
      <nav className="bp-page__subnav" aria-label={t("navigation.aria_label")}>
        <div ref={subnavRef} className="bp-page__subnav-inner">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`bp-page__subnav-link${
                activeSection === link.href.slice(1) ? " is-active" : ""
              }`}
              onClick={(event) => {
                event.preventDefault();
                const targetId = link.href.slice(1);
                const content = contentRef.current;
                const docEl =
                  document.scrollingElement || document.documentElement;
                const isScrollable =
                  content &&
                  content.scrollHeight > content.clientHeight &&
                  window.getComputedStyle(content).overflowY !== "visible";
                const container = isScrollable ? content : window;
                const scrollTop =
                  container === window
                    ? window.scrollY || docEl.scrollTop
                    : content.scrollTop;
                const viewportHeight =
                  container === window
                    ? window.innerHeight
                    : content.clientHeight;
                const target = content
                  ? content.querySelector(`#${targetId}`)
                  : null;
                if (target) {
                  const offset = viewportHeight * 0.4;
                  const targetTop =
                    container === window
                      ? target.getBoundingClientRect().top + scrollTop - offset
                      : target.offsetTop - offset;
                  const scrollTarget = container === window ? window : content;
                  scrollTarget.scrollTo({
                    top: Math.max(0, targetTop),
                    behavior: "smooth",
                  });
                }
                setActiveSection(targetId);
                manualScrollRef.current = true;
                if (manualScrollTimerRef.current) {
                  clearTimeout(manualScrollTimerRef.current);
                }
                manualScrollTimerRef.current = setTimeout(() => {
                  manualScrollRef.current = false;
                }, 400);
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      </nav>
      <div ref={contentRef} className="bp-page__content">
        <div className="bp-page__content-inner">
          <header
            id="overview"
            data-section
            className={`bp-hero bp-section${activeSection === "overview" ? " bp-section--active" : ""}`}
          >
            <div>
              <Heading className="bp-hero__title">
                {t("hero.title")}
              </Heading>
              <Text className="bp-hero__subtitle" paragraph>
                {t("hero.subtitle")}
              </Text>
            </div>
          </header>

          <section
            id="coverage"
            data-section
            className={`bp-card bp-section${activeSection === "coverage" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("coverage.title")}</Heading>
            <Text paragraph className="bp-card__body">
              {t("coverage.body")}
            </Text>
          </section>

          <section
            id="pillars"
            data-section
            className={`bp-card bp-section${activeSection === "pillars" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("pillars.title")}</Heading>
            <div className="bp-benefit-grid" role="list">
              <div className="bp-benefit" role="listitem">
                <h3 className="bp-mini">{t("pillars.payment.title")}</h3>
                <p>
                  {t("pillars.payment.body")}
                </p>
              </div>
              <div className="bp-benefit" role="listitem">
                <h3 className="bp-mini">{t("pillars.authenticity.title")}</h3>
                <p>
                  {t("pillars.authenticity.body")}
                </p>
              </div>
              <div className="bp-benefit" role="listitem">
                <h3 className="bp-mini">{t("pillars.privacy.title")}</h3>
                <p>
                  {t("pillars.privacy.body")}
                </p>
              </div>
            </div>
            <div className="bp-inline-callout" role="note">
              <span className="bp-chip">{t("pillars.callout_label")}</span>
              &nbsp;{t("pillars.callout_body")}
            </div>
          </section>

          <section
            id="flow"
            data-section
            className={`bp-card bp-section${activeSection === "flow" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("flow.title")}</Heading>
            <ol className="bp-ordered">
              <li>
                <strong>{t("flow.purchase.title")}</strong> {t("flow.purchase.body")}
              </li>
              <li>
                <strong>{t("flow.chat.title")}</strong> {t("flow.chat.body")}
              </li>
              <li>
                <strong>{t("flow.cancellation.title")}</strong> {t("flow.cancellation.body")}
              </li>
              <li>
                <strong>{t("flow.delivery.title")}</strong> {t("flow.delivery.body")}
              </li>
              <li>
                <strong>{t("flow.issue.title")}</strong> {t("flow.issue.body")}
              </li>
              <li>
                <strong>{t("flow.mediation.title")}</strong> {t("flow.mediation.body")}
              </li>
              <li>
                <strong>{t("flow.resolution.title")}</strong> {t("flow.resolution.body")}
              </li>
            </ol>
          </section>

          <section
            id="cancellation"
            data-section
            className={`bp-card bp-section${activeSection === "cancellation" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("cancellation.title")}</Heading>
            <Text paragraph className="bp-card__body">
              {t("cancellation.body")}
            </Text>
            <aside className="bp-note" role="note">
              {t("cancellation.note")}
            </aside>
          </section>

          <section
            id="returns"
            data-section
            className={`bp-card bp-section${activeSection === "returns" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("returns.title")}</Heading>
            <ul className="bp-list">
              <li>
                <strong>{t("returns.eligible.title")}</strong> {t("returns.eligible.body")}
              </li>
              <li>
                <strong>{t("returns.ineligible.title")}</strong> {t("returns.ineligible.body")}
              </li>
              <li>
                <strong>{t("returns.mediation.title")}</strong> {t("returns.mediation.body")}
              </li>
            </ul>
            <aside className="bp-note" role="note">
              {t("returns.note_before_originals")} {" "}
              <Link
                className="bp-note__link"
                to="/return-and-refund-policy"
                state={{ fromPath: "/buyer-protection" }}
              >
                {t("returns.originals")}
              </Link>{" "}
              {t("returns.note_between_links")} {" "}
              <Link
                className="bp-note__link"
                to="/limited-editions-return-and-refund-policy"
                state={{ fromPath: "/buyer-protection" }}
              >
                {t("returns.limited_editions")}
              </Link>
              .
            </aside>
          </section>

          <section
            id="resale"
            data-section
            className={`bp-card bp-section${activeSection === "resale" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">
              {t("secondary_market.title")}
            </Heading>
            <ul className="bp-list">
              <li>
                <strong>{t("secondary_market.provenance.title")}</strong> {t("secondary_market.provenance.body")}
              </li>
              <li>
                <strong>{t("secondary_market.royalties.title")}</strong> {t("secondary_market.royalties.body")}
              </li>
              <li>
                <strong>{t("secondary_market.liquidity.title")}</strong> {t("secondary_market.liquidity.body")}
              </li>
              <li>
                <strong>{t("secondary_market.transfer.title")}</strong> {t("secondary_market.transfer.body")}
              </li>
              <li>
                <strong>{t("secondary_market.protection.title")}</strong> {t("secondary_market.protection.body")}
              </li>
            </ul>
            <div className="bp-alert" role="note">
              <span className="bp-alert-dot" aria-hidden="true" />
              <span className="bp-alert__text">
                {t("secondary_market.alert_before")} <strong>{t("secondary_market.alert_emphasis")}</strong>{t("secondary_market.alert_after")}
              </span>
            </div>
          </section>

          <section
            id="shipping"
            data-section
            className={`bp-card bp-section${activeSection === "shipping" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("shipping.title")}</Heading>
            <Text paragraph className="bp-card__body">
              {t("shipping.body")}
            </Text>
          </section>

          <section
            id="privacy"
            data-section
            className={`bp-card bp-section${activeSection === "privacy" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">
              {t("privacy.title")}
            </Heading>
            <Text paragraph className="bp-card__body">
              {t("privacy.body")}
            </Text>
          </section>

          <section
            id="faqs"
            data-section
            className={`bp-card bp-section${activeSection === "faqs" ? " bp-section--active" : ""}`}
          >
            <Heading className="bp-card__title">{t("faq.title")}</Heading>
            <ul className="bp-list bp-list--faq">
              <li>
                <strong>{t("faq.payment.question")}</strong> {t("faq.payment.answer")}
              </li>
              <li>
                <strong>{t("faq.cancellation.question")}</strong> {t("faq.cancellation.answer")}
              </li>
              <li>
                <strong>{t("faq.damage.question")}</strong> {t("faq.damage.answer")}
              </li>
              <li>
                <strong>{t("faq.change_of_mind.question")}</strong> {t("faq.change_of_mind.answer")}
              </li>
              <li>
                <strong>{t("faq.private.question")}</strong> {t("faq.private.answer")}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
